import { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  Group,
  Paper,
  Popover,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { ChevronDown, ChevronUp } from 'react-feather';
import type { Session } from '../../../types';
import classes from './SessionMultiSelect.module.css';

// Color mapping for groups (matching theme colors)
const GROUP_COLORS = ['groupAlpha', 'groupBeta', 'groupGamma', 'medleyGreen', 'surveyPurple'];

// Border colors for group sections (shade 5 of each theme)
const GROUP_BORDER_COLORS: Record<string, string> = {
  groupAlpha: '#8B9B6E',
  groupBeta: '#6E8B9E',
  groupGamma: '#C67A3C',
  medleyGreen: '#4A7C59',
  surveyPurple: '#8E44AD',
};

interface SessionMultiSelectProps {
  sessions: Session[];
  value: string[]; // Array of selected session IDs
  onChange: (sessionIds: string[]) => void;
  isGeneral: boolean; // Whether "General" is selected (no specific sessions)
  onGeneralChange: (isGeneral: boolean) => void;
  label?: string;
  placeholder?: string;
}

interface GroupedSessions {
  allGroups: Session[];
  workshops: Session[];
  byGroup: Map<string, { groupId: string; groupName: string; sessions: Session[] }>;
}

export function SessionMultiSelect({
  sessions,
  value,
  onChange,
  isGeneral,
  onGeneralChange,
  label = 'Session Association',
  placeholder = 'General (no specific session)',
}: SessionMultiSelectProps) {
  const [opened, setOpened] = useState(false);
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Group sessions by group_id, separating workshops
  const groupedSessions = useMemo((): GroupedSessions => {
    const allGroups: Session[] = [];
    const workshops: Session[] = [];
    const byGroup = new Map<string, { groupId: string; groupName: string; sessions: Session[] }>();

    // Sort sessions by session_number
    const sortedSessions = [...sessions].sort((a, b) => a.session_number - b.session_number);

    for (const session of sortedSessions) {
      if (session.session_type === 'workshop_session') {
        // Workshop sessions go to their own section
        workshops.push(session);
      } else if (!session.group_id) {
        // All groups session (group_id is null or undefined)
        allGroups.push(session);
      } else {
        // Group-specific session
        const existing = byGroup.get(session.group_id);
        if (existing) {
          existing.sessions.push(session);
        } else {
          byGroup.set(session.group_id, {
            groupId: session.group_id,
            groupName: session.group_name || `Group ${session.group_id.slice(0, 4)}`,
            sessions: [session],
          });
        }
      }
    }

    return { allGroups, workshops, byGroup };
  }, [sessions]);

  // Build group color mapping (consistent colors per group)
  const groupColors = useMemo(() => {
    const colors = new Map<string, string>();
    let colorIndex = 0;
    for (const groupId of groupedSessions.byGroup.keys()) {
      colors.set(groupId, GROUP_COLORS[colorIndex % GROUP_COLORS.length]);
      colorIndex++;
    }
    return colors;
  }, [groupedSessions.byGroup]);

  // Handle session checkbox change
  const handleSessionChange = (sessionId: string, checked: boolean) => {
    if (checked) {
      // If selecting a session, uncheck "General"
      if (isGeneral) {
        onGeneralChange(false);
      }
      onChange([...value, sessionId]);
    } else {
      const newValue = value.filter((id) => id !== sessionId);
      onChange(newValue);
      // If no sessions selected, default to "General"
      if (newValue.length === 0) {
        onGeneralChange(true);
      }
    }
  };

  // Handle "General" checkbox change
  const handleGeneralChange = (checked: boolean) => {
    onGeneralChange(checked);
    if (checked) {
      // Clear all session selections
      onChange([]);
    }
  };

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Build display text for the dropdown
  const displayText = useMemo(() => {
    if (isGeneral || value.length === 0) {
      return placeholder;
    }

    const selectedSessions = sessions.filter((s) => value.includes(s.id));
    if (selectedSessions.length === 0) {
      return placeholder;
    }

    // Format: "Session 2 – All Groups, Session 4 – Beta Squad, Workshop: Intro"
    const labels = selectedSessions.map((s) => {
      if (s.session_type === 'workshop_session') {
        return s.title || `Workshop ${s.session_number}`;
      }
      if (!s.group_id) {
        return `Session ${s.session_number} – All Groups`;
      }
      return `Session ${s.session_number} – ${s.group_name || 'Group'}`;
    });

    if (labels.length <= 2) {
      return labels.join(', ');
    }
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
  }, [isGeneral, value, sessions, placeholder]);

  return (
    <Box>
      {label && (
        <Text size="sm" fw={500} mb={4}>
          {label}
        </Text>
      )}
      <Popover
        opened={opened}
        onChange={setOpened}
        position="bottom-start"
        width="target"
        shadow="md"
      >
        <Popover.Target>
          <UnstyledButton
            className={classes.trigger}
            onClick={() => setOpened(!opened)}
          >
            <Group justify="space-between" wrap="nowrap">
              <Text size="sm" c={isGeneral ? 'dimmed' : undefined} truncate>
                {displayText}
              </Text>
              {opened ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Group>
          </UnstyledButton>
        </Popover.Target>

        <Popover.Dropdown p={0}>
          <Paper className={classes.dropdown}>
            <Stack gap={0}>
              {/* General checkbox */}
              <Box className={classes.generalRow}>
                <Checkbox
                  label="General (no specific session)"
                  checked={isGeneral}
                  onChange={(e) => handleGeneralChange(e.currentTarget.checked)}
                  classNames={{ label: classes.checkboxLabel }}
                />
              </Box>

              {/* All Groups Section */}
              {groupedSessions.allGroups.length > 0 && (
                <Box className={classes.section}>
                  <UnstyledButton
                    className={classes.sectionHeader}
                    onClick={() => setAllGroupsExpanded(!allGroupsExpanded)}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                        ALL GROUPS
                      </Text>
                      {allGroupsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={allGroupsExpanded}>
                    <Stack gap={0} className={classes.sessionList}>
                      {groupedSessions.allGroups.map((session) => (
                        <Box key={session.id} className={classes.sessionRow}>
                          <Checkbox
                            label={`Session ${session.session_number} – All Groups`}
                            checked={value.includes(session.id)}
                            onChange={(e) => handleSessionChange(session.id, e.currentTarget.checked)}
                            classNames={{ label: classes.checkboxLabel }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}

              {/* Per-Group Sections */}
              {Array.from(groupedSessions.byGroup.entries()).map(([groupId, group]) => {
                const colorName = groupColors.get(groupId) || 'gray';
                const borderColor = GROUP_BORDER_COLORS[colorName] || '#888';
                const isExpanded = expandedGroups.has(groupId);

                return (
                  <Box
                    key={groupId}
                    className={classes.section}
                    style={{ borderLeftColor: borderColor }}
                  >
                    <UnstyledButton
                      className={classes.sectionHeader}
                      onClick={() => toggleGroup(groupId)}
                    >
                      <Group justify="space-between" wrap="nowrap">
                        <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                          {group.groupName.toUpperCase()}
                        </Text>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </Group>
                    </UnstyledButton>
                    <Collapse in={isExpanded}>
                      <Stack gap={0} className={classes.sessionList}>
                        {group.sessions.map((session) => (
                          <Box key={session.id} className={classes.sessionRow}>
                            <Checkbox
                              label={`Session ${session.session_number}`}
                              checked={value.includes(session.id)}
                              onChange={(e) => handleSessionChange(session.id, e.currentTarget.checked)}
                              classNames={{ label: classes.checkboxLabel }}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                );
              })}

              {/* Workshops Section */}
              {groupedSessions.workshops.length > 0 && (
                <Box className={classes.section} style={{ borderLeftColor: '#8E44AD' }}>
                  <UnstyledButton
                    className={classes.sectionHeader}
                    onClick={() => toggleGroup('__workshops__')}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                        WORKSHOPS
                      </Text>
                      {expandedGroups.has('__workshops__') ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </Group>
                  </UnstyledButton>
                  <Collapse in={expandedGroups.has('__workshops__')}>
                    <Stack gap={0} className={classes.sessionList}>
                      {groupedSessions.workshops.map((session) => (
                        <Box key={session.id} className={classes.sessionRow}>
                          <Checkbox
                            label={session.title || `Workshop ${session.session_number}`}
                            checked={value.includes(session.id)}
                            onChange={(e) => handleSessionChange(session.id, e.currentTarget.checked)}
                            classNames={{ label: classes.checkboxLabel }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}
            </Stack>
          </Paper>
        </Popover.Dropdown>
      </Popover>
    </Box>
  );
}
